import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search, MapPin, Filter, Star, Clock, PoundSterling,
  Accessibility, Baby, ArrowLeft, ChevronRight, Calendar,
  Sparkles, Heart, Lock, TreeDeciduous, Building, PartyPopper
} from "lucide-react";
import BottomNav from "@/components/BottomNav";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sample venues data - mix of free and paid partner venues
const SAMPLE_VENUES = [
  {
    id: "free_1",
    name: "Victoria Park",
    type: "park",
    category: "outdoor",
    description: "Beautiful green space with adventure playground, splash pad, and café. Perfect for family picnics!",
    location: "London E9",
    isFree: true,
    accessibility: ["wheelchair_access", "changing_places"],
    ageRange: "All ages",
    imageUrl: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400"
  },
  {
    id: "partner_1",
    name: "Jump Nation Trampoline Park",
    type: "partner",
    category: "soft_play",
    description: "Indoor trampoline park with dedicated toddler sessions, foam pits, and party rooms.",
    location: "Manchester M17",
    isFree: false,
    price: "£12-18",
    accessibility: ["wheelchair_access", "sensory_friendly"],
    ageRange: "3-16 years",
    imageUrl: "https://images.unsplash.com/photo-1564429238877-1f3c1c1f3c1c?w=400"
  },
  {
    id: "free_2", 
    name: "Science Museum",
    type: "museum",
    category: "museum",
    description: "Free entry! Interactive exhibits, Wonderlab for kids, and IMAX cinema. Book timed slots online.",
    location: "London SW7",
    isFree: true,
    accessibility: ["wheelchair_access", "sensory_friendly", "changing_places"],
    ageRange: "All ages",
    imageUrl: "https://images.unsplash.com/photo-1518998053901-5348d3961a04?w=400"
  },
  {
    id: "partner_2",
    name: "Little Splashers Swim School",
    type: "partner", 
    category: "sports",
    description: "Parent & baby swimming classes, toddler lessons, and pool parties. Warm water pools.",
    location: "Birmingham B15",
    isFree: false,
    price: "£15/session",
    accessibility: ["wheelchair_access"],
    ageRange: "6 months - 5 years",
    imageUrl: "https://images.unsplash.com/photo-1576610616656-d3aa5d1f4534?w=400"
  },
  {
    id: "free_3",
    name: "Hampstead Heath Playground",
    type: "park",
    category: "outdoor", 
    description: "Adventure playground with climbing frames, sandpit, and paddling pool in summer.",
    location: "London NW3",
    isFree: true,
    accessibility: ["wheelchair_access"],
    ageRange: "All ages",
    imageUrl: "https://images.unsplash.com/photo-1596997000103-e597b3ca50df?w=400"
  },
  {
    id: "partner_3",
    name: "Pottery Painting Café",
    type: "partner",
    category: "arts",
    description: "Paint your own pottery! Kids parties, drop-in sessions, and school holiday workshops.",
    location: "Bristol BS1",
    isFree: false,
    price: "£8-25",
    accessibility: ["wheelchair_access"],
    ageRange: "3+ years",
    imageUrl: "https://images.unsplash.com/photo-1565193566173-7a0ee3dbe261?w=400"
  },
  {
    id: "free_4",
    name: "Summer Reading Challenge",
    type: "event",
    category: "classes",
    description: "Free at all UK libraries! Kids read 6 books over summer, collect stickers, and get a medal.",
    location: "UK-wide",
    isFree: true,
    accessibility: ["wheelchair_access", "sensory_friendly"],
    ageRange: "4-11 years",
    imageUrl: "https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=400"
  },
  {
    id: "partner_4",
    name: "Party Palace",
    type: "partner",
    category: "party_venue",
    description: "All-inclusive party packages with soft play, disco, and themed rooms. Catering included.",
    location: "Leeds LS2",
    isFree: false,
    price: "From £200",
    accessibility: ["wheelchair_access", "sensory_friendly"],
    ageRange: "1-12 years",
    imageUrl: "https://images.unsplash.com/photo-1530103862676-de8c9debad1d?w=400"
  }
];

const Marketplace = () => {
  const { user } = useAuth();
  const [activities, setActivities] = useState([]);
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeTab, setActiveTab] = useState("activities");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [showAccessibleOnly, setShowAccessibleOnly] = useState(false);

  const categories = [
    { value: "", label: "All" },
    { value: "soft_play", label: "Soft Play" },
    { value: "museum", label: "Museums" },
    { value: "outdoor", label: "Outdoor" },
    { value: "sports", label: "Sports" },
    { value: "arts", label: "Arts & Crafts" },
    { value: "classes", label: "Classes" },
    { value: "party_venue", label: "Party Venues" }
  ];

  useEffect(() => {
    fetchData();
  }, [selectedCategory, showAccessibleOnly]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (selectedCategory) params.category = selectedCategory;
      if (showAccessibleOnly) params.accessibility = "wheelchair_access";

      const [activitiesRes, providersRes] = await Promise.all([
        axios.get(`${API}/activities`, { params }),
        axios.get(`${API}/providers`, { params })
      ]);

      setActivities(activitiesRes.data.activities || []);
      setProviders(providersRes.data.providers || []);
    } catch (error) {
      console.error("Error fetching marketplace data:", error);
    } finally {
      setLoading(false);
    }
  };

  const filteredActivities = activities.filter(a =>
    a.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    a.provider?.business_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredProviders = providers.filter(p =>
    p.business_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.city?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const AccessibilityBadge = ({ features }) => {
    if (!features || features.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1 mt-2">
        {features.includes("wheelchair_access") && (
          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
            <Accessibility className="w-3 h-3" /> Accessible
          </span>
        )}
        {features.includes("sensory_friendly") && (
          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
            Sensory Friendly
          </span>
        )}
        {features.includes("changing_places") && (
          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
            Changing Places
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#FFF9F5] pb-24">
      {/* Header */}
      <div className="hero-gradient text-white p-6 pt-8">
        <div className="flex items-center gap-3 mb-4">
          <Link to="/dashboard" className="p-2 -ml-2 hover:bg-white/10 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Nunito, sans-serif' }}>
              Family Activities
            </h1>
            <p className="text-white/80 text-sm">Discover experiences for your family</p>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search activities, venues..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-12 bg-white/90 border-0 rounded-full text-slate-800"
            data-testid="marketplace-search"
          />
        </div>
      </div>

      {/* Filters */}
      <div className="px-4 py-3 bg-white border-b border-slate-100 sticky top-0 z-10">
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {categories.map(cat => (
            <button
              key={cat.value}
              onClick={() => setSelectedCategory(cat.value)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                selectedCategory === cat.value
                  ? 'bg-purple-500 text-white'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-4 mt-2">
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={showAccessibleOnly}
              onChange={(e) => setShowAccessibleOnly(e.target.checked)}
              className="w-4 h-4 rounded border-slate-300 text-purple-500 focus:ring-purple-500"
            />
            <Accessibility className="w-4 h-4" />
            Wheelchair accessible only
          </label>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-slate-200">
        <button
          onClick={() => setActiveTab("activities")}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === "activities"
              ? 'text-purple-600 border-b-2 border-purple-500'
              : 'text-slate-500'
          }`}
        >
          Activities ({filteredActivities.length})
        </button>
        <button
          onClick={() => setActiveTab("venues")}
          className={`flex-1 py-3 text-sm font-medium transition-colors ${
            activeTab === "venues"
              ? 'text-purple-600 border-b-2 border-purple-500'
              : 'text-slate-500'
          }`}
        >
          Venues ({filteredProviders.length})
        </button>
      </div>

      {/* Content */}
      <div className="p-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : activeTab === "activities" ? (
          /* Show sample venues when no activities from API */
          <div className="space-y-4">
            {/* Info banner */}
            <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 border border-purple-100">
              <h3 className="font-semibold text-purple-800 mb-1">Discover Family-Friendly Places</h3>
              <p className="text-sm text-purple-600">
                Free parks and events are open to all! Partner venues unlock with Premium membership.
              </p>
            </div>

            {/* Sample venues grid */}
            {SAMPLE_VENUES.filter(v => 
              (!selectedCategory || v.category === selectedCategory) &&
              (!showAccessibleOnly || v.accessibility?.includes("wheelchair_access")) &&
              (v.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
               v.description.toLowerCase().includes(searchQuery.toLowerCase()))
            ).map(venue => (
              <div key={venue.id} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100">
                {/* Venue image */}
                <div className="relative h-40">
                  <div 
                    className="w-full h-full bg-cover bg-center"
                    style={{ backgroundImage: `url(${venue.imageUrl})` }}
                  />
                  <div className="absolute top-3 right-3">
                    {venue.isFree ? (
                      <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                        <TreeDeciduous className="w-3 h-3" /> FREE
                      </span>
                    ) : (
                      <span className="bg-purple-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                        <Building className="w-3 h-3" /> Partner
                      </span>
                    )}
                  </div>
                </div>

                <div className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-slate-800">{venue.name}</h3>
                      <p className="text-sm text-slate-500 flex items-center gap-1">
                        <MapPin className="w-3 h-3" /> {venue.location}
                      </p>
                    </div>
                    {!venue.isFree && (
                      <span className="text-sm font-semibold text-purple-600">{venue.price}</span>
                    )}
                  </div>

                  <p className="text-sm text-slate-600 mt-2 line-clamp-2">{venue.description}</p>

                  <div className="flex items-center gap-3 mt-3 text-xs text-slate-500">
                    <span className="flex items-center gap-1">
                      <Baby className="w-3 h-3" /> {venue.ageRange}
                    </span>
                  </div>

                  {/* Accessibility badges */}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {venue.accessibility?.includes("wheelchair_access") && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                        <Accessibility className="w-3 h-3" /> Accessible
                      </span>
                    )}
                    {venue.accessibility?.includes("sensory_friendly") && (
                      <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                        Sensory Friendly
                      </span>
                    )}
                    {venue.accessibility?.includes("changing_places") && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                        Changing Places
                      </span>
                    )}
                  </div>

                  {/* Action button - different for free vs partner */}
                  <div className="mt-4">
                    {venue.isFree ? (
                      <Button className="w-full bg-green-500 hover:bg-green-600 text-white rounded-xl">
                        View Details & Directions
                      </Button>
                    ) : user?.is_premium ? (
                      <Button className="w-full bg-purple-500 hover:bg-purple-600 text-white rounded-xl">
                        View Contact & Book
                      </Button>
                    ) : (
                      <div className="space-y-2">
                        <Button className="w-full bg-slate-100 text-slate-400 rounded-xl cursor-not-allowed" disabled>
                          <Lock className="w-4 h-4 mr-2" /> Contact Hidden
                        </Button>
                        <Link to="/subscription" className="block">
                          <Button variant="outline" className="w-full border-purple-300 text-purple-600 rounded-xl hover:bg-purple-50">
                            Upgrade to Premium to Unlock
                          </Button>
                        </Link>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {/* CTA for partners */}
            <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl p-6 text-center mt-6">
              <PartyPopper className="w-10 h-10 text-purple-500 mx-auto mb-3" />
              <h3 className="font-bold text-purple-800 mb-2">Run a family business?</h3>
              <p className="text-sm text-purple-600 mb-4">
                List your venue on KiddiPlay and reach thousands of local families!
              </p>
              <Link to="/">
                <Button className="bg-purple-500 hover:bg-purple-600 text-white rounded-full px-6">
                  Become a Partner
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          filteredProviders.length === 0 ? (
            <div className="text-center py-12">
              <MapPin className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">No venues found</p>
              <p className="text-slate-400 text-sm mt-1">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredProviders.map(provider => (
                <div key={provider.provider_id} className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold text-slate-800">{provider.business_name}</h3>
                      <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                        <MapPin className="w-3 h-3" />
                        {provider.city}
                      </p>
                    </div>
                    {provider.rating > 0 && (
                      <div className="flex items-center gap-1 text-amber-500">
                        <Star className="w-4 h-4" fill="currentColor" />
                        <span className="text-sm font-medium">{provider.rating}</span>
                      </div>
                    )}
                  </div>
                  
                  <p className="text-sm text-slate-600 mt-2 line-clamp-2">{provider.description}</p>
                  
                  <div className="flex items-center gap-2 mt-3">
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                      {provider.category?.replace("_", " ")}
                    </span>
                    {provider.age_range && (
                      <span className="text-xs bg-pink-100 text-pink-600 px-2 py-1 rounded-full">
                        Ages: {provider.age_range}
                      </span>
                    )}
                  </div>
                  
                  <AccessibilityBadge features={provider.accessibility} />
                  
                  {provider.website && (
                    <a
                      href={provider.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center text-sm text-purple-600 hover:text-purple-700 font-medium"
                    >
                      Visit Website <ChevronRight className="w-4 h-4" />
                    </a>
                  )}
                </div>
              ))}
            </div>
          )
        )}
      </div>

      {/* Info Banner */}
      <div className="mx-4 mt-4 p-4 bg-purple-50 rounded-xl border border-purple-100">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-purple-500 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-purple-800">Want to list your business?</p>
            <p className="text-xs text-purple-600 mt-1">
              Family-friendly venues and activity providers can list on KiddiPlay.
              <Link to="/contact" className="ml-1 underline">Contact us</Link>
            </p>
          </div>
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default Marketplace;
